// Única fonte de verdade sobre quais campos do cadastro do veículo estão de
// fato persistidos no GPS Drive Backend (vehicle_metadata) e quais ainda
// não — usada para montar o payload de PUT (nunca manda uma chave que o
// backend não reconhece, ver updateVehicleMetadataDto.strict() em
// gpsdrive-backend). O popup "Resultado do salvamento" que antes lia
// PENDING_CADASTRO_FIELDS pra mostrar isso na tela foi removido 2026-09-01
// (pedido do usuário); esta lista continua valendo só para montar o payload.
export type CadastroFieldGroup = "principal" | "veiculo" | "rastreador" | "instalacao";

export interface CadastroFieldDef {
  // Chave do payload de UpdateVehicleMetadataInput (para kind "field") ou só
  // um identificador estável (para kind "image", que vai por upload
  // separado, nunca dentro do PUT).
  key: string;
  label: string;
  group: CadastroFieldGroup;
  persisted: boolean;
  kind?: "field" | "image";
}

export const VEHICLE_CADASTRO_FIELDS: CadastroFieldDef[] = [
  // Dados principais
  { key: "customName", label: "Descrição", group: "principal", persisted: true },
  { key: "brand", label: "Marca", group: "principal", persisted: true },
  { key: "model", label: "Modelo", group: "principal", persisted: true },
  { key: "year", label: "Ano", group: "principal", persisted: true },
  { key: "planId", label: "Plano", group: "principal", persisted: true },
  { key: "vehicleImage", label: "Foto do veículo", group: "principal", persisted: true, kind: "image" },

  // Dados do veículo
  { key: "color", label: "Cor", group: "veiculo", persisted: true },
  { key: "fipeValue", label: "Valor FIPE", group: "veiculo", persisted: true },
  { key: "chassis", label: "Chassi", group: "veiculo", persisted: true },
  { key: "notes", label: "Observações", group: "veiculo", persisted: true },

  // Rastreador
  { key: "trackerSupplier", label: "Fornecedor", group: "rastreador", persisted: true },
  { key: "simCarrier", label: "Operadora", group: "rastreador", persisted: true },
  { key: "simIccid", label: "ICCID", group: "rastreador", persisted: true },
  { key: "simNumber", label: "Número do SIM", group: "rastreador", persisted: true },
  { key: "simApn", label: "APN", group: "rastreador", persisted: true },
  { key: "trackerBrand", label: "Fabricante do rastreador", group: "rastreador", persisted: true },
  { key: "trackerModel", label: "Modelo do rastreador", group: "rastreador", persisted: true },
  { key: "trackerPrice", label: "Valor do equipamento", group: "rastreador", persisted: true },
  { key: "protocol", label: "Protocolo do rastreador", group: "rastreador", persisted: true },
  { key: "cameraImei", label: "IMEI da câmera", group: "rastreador", persisted: true },
  { key: "trackerType", label: "Tipo de rastreador", group: "rastreador", persisted: true },

  // Instalação
  { key: "installCompany", label: "Empresa da instalação", group: "instalacao", persisted: true },
  { key: "installTechnician", label: "Instalador", group: "instalacao", persisted: true },
  { key: "installedAt", label: "Data de instalação", group: "instalacao", persisted: true },
  { key: "installLocation", label: "Local da instalação", group: "instalacao", persisted: true },
  { key: "installWarranty", label: "Garantia", group: "instalacao", persisted: true },
  { key: "installNotes", label: "Observações da instalação", group: "instalacao", persisted: true },
  {
    key: "installImage",
    label: "Foto da instalação",
    group: "instalacao",
    persisted: true,
    kind: "image",
  },

  // Vincular usuário NÃO entra mais aqui: é um vínculo real via GPS Drive
  // Backend → Traccar (ver src/services/vehicle-permissions.service.ts),
  // fora do payload de vehicle_metadata e fora do fluxo de "Salvar
  // alterações" que este relatório descreve — cada toggle já sincroniza na
  // hora, independente deste form.
];

// Helpers usados por VehicleFormPanel (routes/index.tsx — cadastro e edição
// compartilham a mesma implementação) para converter string de input em
// valor de payload — nunca manda string vazia como se fosse um valor real.
export function toNullableString(v: string): string | null {
  return v.trim() ? v.trim() : null;
}
export function toNullableNumber(v: string): number | null {
  return v.trim() ? Number(v) : null;
}

// Monta o payload de PUT só com as chaves persisted:true de kind "field" —
// nunca inclui campos de imagem (vão por upload separado) nem campos
// pendentes (evita 422 do .strict() do backend por engano).
export function buildMetadataPayload<T extends Record<string, unknown>>(
  form: T,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const field of VEHICLE_CADASTRO_FIELDS) {
    if (!field.persisted || field.kind === "image") continue;
    if (field.key in form) payload[field.key] = form[field.key];
  }
  return payload;
}
